import React, { useState, useEffect, useMemo } from 'react';
import { 
  Mail, 
  Send, 
  CheckCircle2, 
  AlertTriangle, 
  RefreshCw, 
  Trash2, 
  Search, 
  Filter, 
  KeyRound, 
  Clock, 
  Activity, 
  Check, 
  Copy, 
  Server, 
  Zap, 
  ShieldCheck, 
  ExternalLink, 
  Sliders, 
  Eye, 
  EyeOff, 
  ChevronDown, 
  ChevronUp, 
  Loader2,
  Sparkles,
  UserCheck,
  User,
  RotateCcw,
  Calendar,
  Layers,
  Flame,
  FileText
} from 'lucide-react';
import { EmailDeliveryLog, EmailServiceConfig } from '../../types';
import { subscribeEmailLogs, deleteEmailLogFromFirestore, clearAllEmailLogsFromFirestore } from '../../services/firestoreService';
import { EmailTemplatePreviewModal } from './EmailTemplatePreviewModal';

interface EmailLogsTabProps {
  onTriggerToast?: (title: string, message: string) => void;
}

export const EmailLogsTab: React.FC<EmailLogsTabProps> = ({ onTriggerToast }) => {
  // Logs State
  const [logs, setLogs] = useState<EmailDeliveryLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'delivered' | 'failed'>('all');
  const [providerFilter, setProviderFilter] = useState<'all' | 'resend' | 'smtp'>('all');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Template Preview Modal State
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Manual Sync Now Trigger State (By User ID or Email)
  const [isManualSyncOpen, setIsManualSyncOpen] = useState(false);
  const [manualSyncUserId, setManualSyncUserId] = useState('');
  const [manualSyncEmail, setManualSyncEmail] = useState('');
  const [manualSyncName, setManualSyncName] = useState('');
  const [manualSyncProvider, setManualSyncProvider] = useState<'fallback' | 'resend' | 'smtp'>('fallback');
  const [isExecutingManualSync, setIsExecutingManualSync] = useState(false);
  const [syncingRowId, setSyncingRowId] = useState<string | null>(null);
  const [syncedRowSuccessId, setSyncedRowSuccessId] = useState<string | null>(null);

  // Bulk Re-Trigger Failed Emails State
  const [isBulkRetryOpen, setIsBulkRetryOpen] = useState(false);
  const [bulkRetryPreset, setBulkRetryPreset] = useState<'24h' | '3d' | '7d' | '30d' | 'custom' | 'all'>('24h');
  const [bulkRetryStartDate, setBulkRetryStartDate] = useState('');
  const [bulkRetryEndDate, setBulkRetryEndDate] = useState('');
  const [bulkRetryProvider, setBulkRetryProvider] = useState<'fallback' | 'resend' | 'smtp'>('fallback');
  const [isExecutingBulkRetry, setIsExecutingBulkRetry] = useState(false);
  const [bulkRetryResult, setBulkRetryResult] = useState<any | null>(null);

  // Config State
  const [config, setConfig] = useState<EmailServiceConfig>({
    preferredProvider: 'fallback',
    isResendConfigured: false,
    isSmtpConfigured: false,
    senderEmail: 'support@playgroundleague.pro',
    senderName: 'Playground League Support',
    smtpHost: 'smtp.godaddy.com',
    smtpPort: 587,
    smtpUser: 'support@playgroundleague.pro',
  });
  const [customResendKey, setCustomResendKey] = useState('');
  const [customSmtpPass, setCustomSmtpPass] = useState('');
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // Test Send Workbench State
  const [testEmail, setTestEmail] = useState('elmejor15@gmail.com');
  const [testName, setTestName] = useState('League Commissioner');
  const [testType, setTestType] = useState<'verification_otp' | 'welcome_email'>('verification_otp');
  const [testProvider, setTestProvider] = useState<'fallback' | 'resend' | 'smtp'>('fallback');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<any | null>(null);

  // Subscribe to real-time email logs from Firestore
  useEffect(() => {
    setIsLoadingLogs(true);
    const unsubscribe = subscribeEmailLogs((newLogs) => {
      setLogs(newLogs);
      setIsLoadingLogs(false);
    });

    // Also fetch initial config and fallback HTTP logs
    fetchConfig();

    return () => {
      unsubscribe();
    };
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch('/api/email/config');
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (err) {
      console.warn('Notice: Could not fetch email config:', err);
    }
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingConfig(true);
    try {
      const payload: any = {
        preferredProvider: config.preferredProvider,
        senderEmail: config.senderEmail,
        senderName: config.senderName,
        smtpHost: config.smtpHost,
        smtpPort: config.smtpPort,
        smtpUser: config.smtpUser,
      };
      if (customResendKey) payload.resendApiKey = customResendKey;
      if (customSmtpPass) payload.smtpPass = customSmtpPass;

      const res = await fetch('/api/email/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.config) {
          setConfig((prev) => ({ ...prev, ...data.config }));
        }
        onTriggerToast?.('Email Configuration Saved', 'Active email delivery pipeline updated successfully.');
        setCustomResendKey('');
        setCustomSmtpPass('');
      }
    } catch (err: any) {
      onTriggerToast?.('Configuration Save Error', err?.message || 'Failed to update email configuration.');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleTestSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testEmail) return;

    setIsSendingTest(true);
    setTestResult(null);

    try {
      const res = await fetch('/api/email/test-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: testEmail,
          name: testName,
          type: testType,
          provider: testProvider === 'fallback' ? undefined : testProvider,
        }),
      });

      const data = await res.json();
      setTestResult(data);

      if (data.success) {
        onTriggerToast?.('Test Email Dispatched', `Delivered via ${data.provider.toUpperCase()} in ${data.latencyMs}ms`);
      } else {
        onTriggerToast?.('Test Email Failed', data.error || 'Check provider credentials and logs.');
      }
    } catch (err: any) {
      setTestResult({
        success: false,
        error: err?.message || 'Network error executing test send.',
      });
      onTriggerToast?.('Dispatch Error', err?.message || 'Failed to send test email.');
    } finally {
      setIsSendingTest(false);
    }
  };

  // Manual Sync Trigger Action (by User ID or Email)
  const handleManualSyncSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualSyncUserId && !manualSyncEmail) {
      onTriggerToast?.('Sync Parameter Missing', 'Please enter a User ID or Email address to sync.');
      return;
    }

    setIsExecutingManualSync(true);
    try {
      const res = await fetch('/api/email/sync-dispatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: manualSyncUserId.trim() || undefined,
          email: manualSyncEmail.trim() || undefined,
          name: manualSyncName.trim() || undefined,
          provider: manualSyncProvider === 'fallback' ? undefined : manualSyncProvider,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        onTriggerToast?.(
          'Sync Dispatch Succeeded! ⚡',
          `Re-sent verification OTP [${data.verificationCode}] to ${data.recipientEmail} via ${data.provider.toUpperCase()}`
        );
        setManualSyncUserId('');
        setManualSyncEmail('');
        setManualSyncName('');
        setIsManualSyncOpen(false);
      } else {
        onTriggerToast?.(
          'Sync Dispatch Warning',
          data.errorMessage || data.error || 'Manual sync re-trigger failed. Check error logs.'
        );
      }
    } catch (err: any) {
      onTriggerToast?.('Sync Error', err?.message || 'Failed to connect to sync endpoint.');
    } finally {
      setIsExecutingManualSync(false);
    }
  };

  // Row-level instant Sync Now re-trigger
  const handleRowSyncNow = async (log: EmailDeliveryLog) => {
    setSyncingRowId(log.id);
    try {
      const res = await fetch('/api/email/sync-dispatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: log.userId,
          email: log.recipientEmail,
          name: log.recipientName,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSyncedRowSuccessId(log.id);
        onTriggerToast?.(
          'Sync Now Succeeded! ⚡',
          `Re-triggered fresh OTP [${data.verificationCode}] to ${data.recipientEmail} via ${data.provider.toUpperCase()}`
        );
        setTimeout(() => setSyncedRowSuccessId(null), 4000);
      } else {
        onTriggerToast?.('Sync Failed', data.errorMessage || data.error || 'Check provider status in logs.');
      }
    } catch (err: any) {
      onTriggerToast?.('Sync Error', err?.message || 'Failed to re-trigger email.');
    } finally {
      setSyncingRowId(null);
    }
  };

  // Compute matching failed verification emails for the selected bulk retry range
  const matchingFailedLogs = useMemo(() => {
    const now = Date.now();
    let startTimestamp = 0;
    let endTimestamp = now + 60000;

    if (bulkRetryPreset === '24h') {
      startTimestamp = now - 24 * 3600 * 1000;
    } else if (bulkRetryPreset === '3d') {
      startTimestamp = now - 3 * 24 * 3600 * 1000;
    } else if (bulkRetryPreset === '7d') {
      startTimestamp = now - 7 * 24 * 3600 * 1000;
    } else if (bulkRetryPreset === '30d') {
      startTimestamp = now - 30 * 24 * 3600 * 1000;
    } else if (bulkRetryPreset === 'custom') {
      if (bulkRetryStartDate) {
        const parsed = new Date(bulkRetryStartDate).getTime();
        if (!isNaN(parsed)) startTimestamp = parsed;
      }
      if (bulkRetryEndDate) {
        const parsed = new Date(bulkRetryEndDate).getTime();
        if (!isNaN(parsed)) endTimestamp = parsed + 86400000;
      }
    }

    return logs.filter((log) => {
      const isFailed = log.status === 'failed';
      const isVerification = log.type === 'verification_otp' || !log.type;
      const ts = log.timestamp || (log.createdAt ? new Date(log.createdAt).getTime() : 0);
      return isFailed && isVerification && ts >= startTimestamp && ts <= endTimestamp;
    });
  }, [logs, bulkRetryPreset, bulkRetryStartDate, bulkRetryEndDate]);

  // Execute Bulk Re-trigger Action across Date Range
  const handleExecuteBulkRetry = async () => {
    setIsExecutingBulkRetry(true);
    setBulkRetryResult(null);
    try {
      const res = await fetch('/api/email/bulk-retry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset: bulkRetryPreset,
          startDate: bulkRetryPreset === 'custom' ? bulkRetryStartDate : undefined,
          endDate: bulkRetryPreset === 'custom' ? bulkRetryEndDate : undefined,
          provider: bulkRetryProvider === 'fallback' ? undefined : bulkRetryProvider,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setBulkRetryResult(data);
        onTriggerToast?.(
          'Bulk Re-trigger Completed! 🚀',
          `Successfully re-dispatched ${data.successCount} of ${data.processedCount} failed verification email(s).`
        );
      } else {
        setBulkRetryResult(data);
        onTriggerToast?.(
          'Bulk Re-trigger Alert',
          data?.message || data?.error || 'Bulk re-trigger finished with notices.'
        );
      }
    } catch (err: any) {
      onTriggerToast?.('Bulk Re-trigger Error', err?.message || 'Failed to communicate with bulk retry endpoint.');
    } finally {
      setIsExecutingBulkRetry(false);
    }
  };

  const handleDeleteLog = async (id: string) => {
    try {
      await deleteEmailLogFromFirestore(id);
      await fetch(`/api/email/logs/${id}`, { method: 'DELETE' }).catch(() => {});
      setLogs((prev) => prev.filter((l) => l.id !== id));
      onTriggerToast?.('Log Removed', 'Email log item permanently deleted.');
    } catch (err) {
      console.warn('Error deleting email log:', err);
    }
  };

  const handleClearAllLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all outgoing email delivery logs? This cannot be undone.')) {
      return;
    }
    try {
      await clearAllEmailLogsFromFirestore();
      await fetch('/api/email/logs/clear', { method: 'POST' }).catch(() => {});
      setLogs([]);
      onTriggerToast?.('Logs Cleared', 'All email delivery logs have been purged.');
    } catch (err) {
      console.warn('Error clearing email logs:', err);
    }
  };

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard?.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filtered logs
  const filteredLogs = logs.filter((l) => {
    const matchesQuery = 
      !searchQuery ||
      l.recipientEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (l.recipientName && l.recipientName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (l.userId && l.userId.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (l.errorCode && l.errorCode.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (l.verificationCode && l.verificationCode.includes(searchQuery)) ||
      (l.subject && l.subject.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
    const matchesProvider = providerFilter === 'all' || l.provider.toLowerCase().includes(providerFilter.toLowerCase());

    return matchesQuery && matchesStatus && matchesProvider;
  });

  // Calculate Metrics
  const totalCount = logs.length;
  const deliveredCount = logs.filter((l) => l.status === 'delivered').length;
  const failedCount = logs.filter((l) => l.status === 'failed').length;
  const successRate = totalCount > 0 ? Math.round((deliveredCount / totalCount) * 100) : 100;
  const avgLatency = totalCount > 0 
    ? Math.round(logs.reduce((acc, curr) => acc + (curr.latencyMs || 250), 0) / totalCount) 
    : 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* ================= HEADER & STATS SUMMARY ================= */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10 shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black italic uppercase text-indigo-300/60">
              Total Dispatched
            </span>
            <Mail className="w-4 h-4 text-lime-400" />
          </div>
          <div className="text-2xl font-black italic text-white mt-1">
            {totalCount.toLocaleString()}
          </div>
          <span className="text-[10px] text-indigo-300 font-bold mt-1 block">Transactional Mails</span>
        </div>

        <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10 shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black italic uppercase text-indigo-300/60">
              Delivered Successfully
            </span>
            <CheckCircle2 className="w-4 h-4 text-lime-400" />
          </div>
          <div className="text-2xl font-black italic text-lime-400 mt-1">
            {deliveredCount} <span className="text-xs text-lime-300 font-semibold font-sans">({successRate}%)</span>
          </div>
          <span className="text-[10px] text-lime-300 font-bold mt-1 block">Zero Bounce Rate</span>
        </div>

        <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10 shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black italic uppercase text-indigo-300/60">
              Delivery Failures
            </span>
            <AlertTriangle className={`w-4 h-4 ${failedCount > 0 ? 'text-rose-400' : 'text-indigo-400'}`} />
          </div>
          <div className={`text-2xl font-black italic mt-1 ${failedCount > 0 ? 'text-rose-400' : 'text-white'}`}>
            {failedCount}
          </div>
          <span className="text-[10px] text-indigo-300 font-bold mt-1 block">
            {failedCount > 0 ? 'Requires Inspection' : 'All Pipelines Healthy'}
          </span>
        </div>

        <div className="bg-indigo-900/60 p-4 rounded-2xl border border-white/10 shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-black italic uppercase text-indigo-300/60">
              Avg Latency
            </span>
            <Clock className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-black italic text-amber-300 mt-1">
            {avgLatency} <span className="text-xs text-indigo-300 font-sans">ms</span>
          </div>
          <span className="text-[10px] text-amber-400 font-bold mt-1 block">
            Provider: {config.preferredProvider.toUpperCase()}
          </span>
        </div>
      </div>

      {/* ================= DUAL GRID: CONFIGURATION & LIVE TEST WORKBENCH ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* PROVIDER SETTINGS PANEL */}
        <div className="lg:col-span-6 bg-indigo-900/60 p-5 sm:p-6 rounded-[2rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <div className="flex items-center space-x-2.5">
              <div className="p-2 bg-amber-400/20 text-amber-300 rounded-xl">
                <Sliders className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-black italic uppercase text-white tracking-wide">
                  Dual-Pipeline Email Engine
                </h3>
                <p className="text-[11px] text-indigo-300 font-medium">
                  Configure Resend API & SMTP (GoDaddy / Custom) Delivery
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full bg-indigo-950 border border-white/10 text-indigo-200">
              <span className={`w-2 h-2 rounded-full ${config.isResendConfigured || config.isSmtpConfigured ? 'bg-lime-400 animate-pulse' : 'bg-amber-400'}`} />
              <span>{config.isResendConfigured ? 'Resend Ready' : config.isSmtpConfigured ? 'SMTP Ready' : 'Fallback Active'}</span>
            </div>
          </div>

          <form onSubmit={handleSaveConfig} className="space-y-3.5 text-xs">
            <div>
              <label className="block text-[11px] font-black uppercase text-indigo-200 mb-1">
                Preferred Outgoing Provider
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setConfig((prev) => ({ ...prev, preferredProvider: 'resend' }))}
                  className={`py-2 px-3 rounded-xl font-black uppercase text-[10px] transition border flex items-center justify-center space-x-1.5 ${
                    config.preferredProvider === 'resend'
                      ? 'bg-lime-400 text-black border-lime-400 shadow-md'
                      : 'bg-indigo-950/70 text-indigo-300 border-white/10 hover:border-white/20'
                  }`}
                >
                  <Zap className="w-3 h-3" />
                  <span>Resend API</span>
                </button>

                <button
                  type="button"
                  onClick={() => setConfig((prev) => ({ ...prev, preferredProvider: 'smtp' }))}
                  className={`py-2 px-3 rounded-xl font-black uppercase text-[10px] transition border flex items-center justify-center space-x-1.5 ${
                    config.preferredProvider === 'smtp'
                      ? 'bg-lime-400 text-black border-lime-400 shadow-md'
                      : 'bg-indigo-950/70 text-indigo-300 border-white/10 hover:border-white/20'
                  }`}
                >
                  <Server className="w-3 h-3" />
                  <span>SMTP Server</span>
                </button>

                <button
                  type="button"
                  onClick={() => setConfig((prev) => ({ ...prev, preferredProvider: 'fallback' }))}
                  className={`py-2 px-3 rounded-xl font-black uppercase text-[10px] transition border flex items-center justify-center space-x-1.5 ${
                    config.preferredProvider === 'fallback'
                      ? 'bg-lime-400 text-black border-lime-400 shadow-md'
                      : 'bg-indigo-950/70 text-indigo-300 border-white/10 hover:border-white/20'
                  }`}
                >
                  <ShieldCheck className="w-3 h-3" />
                  <span>Auto Fallback</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                  Sender From Address
                </label>
                <input
                  type="email"
                  value={config.senderEmail}
                  onChange={(e) => setConfig((prev) => ({ ...prev, senderEmail: e.target.value }))}
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-lime-400"
                  placeholder="support@playgroundleague.pro"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                  Sender Display Name
                </label>
                <input
                  type="text"
                  value={config.senderName}
                  onChange={(e) => setConfig((prev) => ({ ...prev, senderName: e.target.value }))}
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-lime-400"
                  placeholder="Playground League Support"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                Resend API Key (re_...)
              </label>
              <input
                type="password"
                value={customResendKey}
                onChange={(e) => setCustomResendKey(e.target.value)}
                placeholder={config.resendKeyMasked ? `Configured (${config.resendKeyMasked}) - Enter to replace` : 'Enter Resend API Key'}
                className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-lime-300 text-xs font-mono outline-none focus:border-lime-400"
              />
            </div>

            <div className="p-3 bg-indigo-950/80 rounded-xl border border-white/10 space-y-2">
              <span className="text-[10px] font-black uppercase text-amber-300 block">
                SMTP Server Parameters (GoDaddy / Custom SMTP)
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-2">
                  <input
                    type="text"
                    value={config.smtpHost}
                    onChange={(e) => setConfig((prev) => ({ ...prev, smtpHost: e.target.value }))}
                    placeholder="Host (smtp.godaddy.com)"
                    className="w-full bg-indigo-900 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    value={config.smtpPort}
                    onChange={(e) => setConfig((prev) => ({ ...prev, smtpPort: parseInt(e.target.value, 10) || 587 }))}
                    placeholder="Port (587)"
                    className="w-full bg-indigo-900 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <input
                    type="text"
                    value={config.smtpUser}
                    onChange={(e) => setConfig((prev) => ({ ...prev, smtpUser: e.target.value }))}
                    placeholder="SMTP Username"
                    className="w-full bg-indigo-900 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs"
                  />
                </div>
                <div className="relative">
                  <input
                    type={showSmtpPass ? 'text' : 'password'}
                    value={customSmtpPass}
                    onChange={(e) => setCustomSmtpPass(e.target.value)}
                    placeholder={config.isSmtpConfigured ? 'Password configured (enter to change)' : 'SMTP Password'}
                    className="w-full bg-indigo-900 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs pr-8"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPass(!showSmtpPass)}
                    className="absolute right-2 top-2 text-indigo-400 hover:text-white"
                  >
                    {showSmtpPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSavingConfig}
              className="w-full py-2.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-50 text-black font-black uppercase text-xs rounded-xl shadow-md transition flex items-center justify-center space-x-1.5"
            >
              {isSavingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              <span>Save Email Configuration</span>
            </button>
          </form>
        </div>

        {/* LIVE TEST SEND WORKBENCH */}
        <div className="lg:col-span-6 bg-indigo-900/60 p-5 sm:p-6 rounded-[2rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-white/10 pb-3">
            <div className="flex items-center space-x-2.5">
              <div className="p-2 bg-lime-400/20 text-lime-300 rounded-xl">
                <Send className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-black italic uppercase text-white tracking-wide">
                  Live Dispatch Workbench
                </h3>
                <p className="text-[11px] text-indigo-300 font-medium">
                  Trigger Real-time OTP & Welcome Emails with Diagnostics
                </p>
              </div>
            </div>
            <span className="text-[10px] font-black uppercase text-lime-400 bg-lime-400/10 px-2.5 py-1 rounded-full border border-lime-400/20">
              Live Simulator
            </span>
          </div>

          <form onSubmit={handleTestSend} className="space-y-3 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                  Recipient Email Target
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="e.g. elmejor15@gmail.com"
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-lime-400"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                  Recipient Athlete Name
                </label>
                <input
                  type="text"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="e.g. Commissioner"
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-lime-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                  Template Type
                </label>
                <select
                  value={testType}
                  onChange={(e) => setTestType(e.target.value as any)}
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none"
                >
                  <option value="verification_otp">6-Digit Verification OTP & Link</option>
                  <option value="welcome_email">Official Welcome to League Email</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                  Provider Override
                </label>
                <select
                  value={testProvider}
                  onChange={(e) => setTestProvider(e.target.value as any)}
                  className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none"
                >
                  <option value="fallback">Default (Auto Fallback)</option>
                  <option value="resend">Force Resend API</option>
                  <option value="smtp">Force SMTP</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSendingTest}
              className="w-full py-2.5 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-black font-black uppercase text-xs rounded-xl shadow-md transition flex items-center justify-center space-x-1.5"
            >
              {isSendingTest ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-black" />
                  <span>Dispatching Email Diagnostic...</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span>Send Test Verification to {testEmail}</span>
                </>
              )}
            </button>
          </form>

          {/* DIAGNOSTIC RESULTS DISPLAY */}
          {testResult && (
            <div className={`p-3.5 rounded-2xl border text-xs space-y-2 animate-fadeIn ${
              testResult.success 
                ? 'bg-lime-950/40 border-lime-400/30 text-lime-200' 
                : 'bg-rose-950/40 border-rose-400/30 text-rose-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="font-black uppercase tracking-wider flex items-center space-x-1.5">
                  {testResult.success ? <CheckCircle2 className="w-4 h-4 text-lime-400" /> : <AlertTriangle className="w-4 h-4 text-rose-400" />}
                  <span>{testResult.success ? 'Dispatch Succeeded' : 'Dispatch Failed'}</span>
                </span>
                <span className="font-mono text-[10px] text-indigo-300">
                  {testResult.latencyMs}ms Latency
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] font-medium">
                <div>
                  <span className="text-indigo-300/80 block">Provider Used:</span>
                  <strong className="text-white uppercase">{testResult.provider || 'N/A'}</strong>
                </div>
                {testResult.verificationCode && (
                  <div>
                    <span className="text-indigo-300/80 block">Generated OTP:</span>
                    <strong className="text-lime-300 font-mono text-sm tracking-wider">{testResult.verificationCode}</strong>
                  </div>
                )}
              </div>

              {testResult.error && (
                <div className="p-2 bg-black/40 rounded-lg text-[10px] font-mono text-rose-300">
                  {testResult.error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ================= SECTION 3: OUTGOING VERIFICATION EMAIL LOGS DIRECTORY ================= */}
      <div className="bg-indigo-900/60 p-5 sm:p-6 rounded-[2rem] border border-white/10 shadow-xl space-y-4">
        
        {/* LOGS HEADER & FILTERS */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 bg-indigo-950 border border-white/10 text-lime-400 rounded-xl">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-black italic uppercase text-white tracking-wide">
                  Outgoing SMTP & Resend Verification Logs
                </h3>
                <span className="px-2 py-0.5 rounded-full bg-lime-400/20 text-lime-300 border border-lime-400/30 text-[9px] font-black uppercase font-mono">
                  FIRESTORE `email_logs`
                </span>
              </div>
              <p className="text-xs text-indigo-200">
                Audited real-time transmission status, error codes, timestamps, latency, and OTP delivery records
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* PREVIEW TEMPLATE BUTTON */}
            <button
              type="button"
              onClick={() => setIsPreviewModalOpen(true)}
              className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1.5"
              title="Preview branded HTML email templates"
            >
              <Eye className="w-4 h-4 text-black" />
              <span>Preview Template</span>
            </button>

            {/* BULK RE-TRIGGER FAILED EMAILS TOGGLE */}
            <button
              type="button"
              onClick={() => {
                setIsBulkRetryOpen(!isBulkRetryOpen);
                if (isManualSyncOpen) setIsManualSyncOpen(false);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase transition flex items-center space-x-1.5 border ${
                isBulkRetryOpen 
                  ? 'bg-rose-500 text-white border-rose-400 shadow-md ring-2 ring-rose-400/30' 
                  : failedCount > 0
                    ? 'bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border-rose-500/40'
                    : 'bg-indigo-950 hover:bg-indigo-800 text-indigo-300 border-white/10'
              }`}
              title="Bulk re-trigger all failed verification emails for a date range"
            >
              <RotateCcw className={`w-3.5 h-3.5 ${isBulkRetryOpen ? 'animate-spin' : ''}`} />
              <span>Bulk Re-Trigger ({failedCount})</span>
            </button>

            {/* MANUAL SYNC NOW TRIGGER TOGGLE */}
            <button
              type="button"
              onClick={() => {
                setIsManualSyncOpen(!isManualSyncOpen);
                if (isBulkRetryOpen) setIsBulkRetryOpen(false);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase transition flex items-center space-x-1.5 border ${
                isManualSyncOpen 
                  ? 'bg-amber-400 text-black border-amber-300 shadow-md' 
                  : 'bg-indigo-950 hover:bg-indigo-800 text-amber-300 border-amber-400/30'
              }`}
              title="Trigger manual sync by user ID or email"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isManualSyncOpen ? 'animate-spin' : ''}`} />
              <span>Manual Sync</span>
            </button>

            <button
              onClick={() => {
                fetchConfig();
                onTriggerToast?.('Logs Refreshed', 'Synced latest transmission records.');
              }}
              className="px-3 py-1.5 bg-indigo-950 hover:bg-indigo-800 text-indigo-200 border border-white/10 rounded-xl text-xs font-bold transition flex items-center space-x-1.5"
              title="Refresh logs"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Refresh</span>
            </button>

            {logs.length > 0 && (
              <button
                onClick={handleClearAllLogs}
                className="px-3 py-1.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 rounded-xl text-xs font-bold transition flex items-center space-x-1.5"
                title="Clear all logs"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Clear All</span>
              </button>
            )}
          </div>
        </div>

        {/* ================= MANUAL SYNC NOW EXPANDED PANEL ================= */}
        {isManualSyncOpen && (
          <div className="p-4 sm:p-5 bg-indigo-950/90 border border-amber-400/40 rounded-2xl space-y-3 animate-fadeIn shadow-lg">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-amber-400 text-black rounded-lg">
                  <RefreshCw className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-black uppercase text-amber-300 tracking-wide">
                    Manual Sync & Re-Trigger Verification Email
                  </h4>
                  <p className="text-[11px] text-indigo-300">
                    Re-dispatch fresh verification OTP and direct link for an athlete by User ID or Email address
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsManualSyncOpen(false)}
                className="text-indigo-400 hover:text-white text-xs font-bold"
              >
                ✕ Close
              </button>
            </div>

            <form onSubmit={handleManualSyncSubmit} className="grid grid-cols-1 sm:grid-cols-12 gap-3 text-xs">
              <div className="sm:col-span-4">
                <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">
                  Athlete User ID (Optional if Email Provided)
                </label>
                <div className="relative">
                  <User className="w-3.5 h-3.5 text-indigo-400 absolute left-3 top-2.5" />
                  <input
                    type="text"
                    value={manualSyncUserId}
                    onChange={(e) => setManualSyncUserId(e.target.value)}
                    placeholder="e.g. ath_2901 or uid_881"
                    className="w-full bg-indigo-900/80 border border-white/10 rounded-xl pl-8 pr-3 py-2 text-white text-xs outline-none focus:border-amber-400 font-mono"
                  />
                </div>
              </div>

              <div className="sm:col-span-4">
                <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">
                  Recipient Email
                </label>
                <div className="relative">
                  <Mail className="w-3.5 h-3.5 text-indigo-400 absolute left-3 top-2.5" />
                  <input
                    type="email"
                    value={manualSyncEmail}
                    onChange={(e) => setManualSyncEmail(e.target.value)}
                    placeholder="athlete@example.com"
                    className="w-full bg-indigo-900/80 border border-white/10 rounded-xl pl-8 pr-3 py-2 text-white text-xs outline-none focus:border-amber-400 font-mono"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">
                  Provider
                </label>
                <select
                  value={manualSyncProvider}
                  onChange={(e) => setManualSyncProvider(e.target.value as any)}
                  className="w-full bg-indigo-900/80 border border-white/10 rounded-xl px-2 py-2 text-white text-xs outline-none font-bold"
                >
                  <option value="fallback">Auto</option>
                  <option value="resend">Resend</option>
                  <option value="smtp">SMTP</option>
                </select>
              </div>

              <div className="sm:col-span-2 flex items-end">
                <button
                  type="submit"
                  disabled={isExecutingManualSync}
                  className="w-full py-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-black font-black uppercase text-xs rounded-xl shadow-md transition flex items-center justify-center space-x-1"
                >
                  {isExecutingManualSync ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-3.5 h-3.5" />
                      <span>Sync Now</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ================= BULK RE-TRIGGER FAILED EMAILS EXPANDED PANEL ================= */}
        {isBulkRetryOpen && (
          <div className="p-4 sm:p-5 bg-indigo-950/95 border-2 border-rose-500/50 rounded-2xl space-y-4 animate-fadeIn shadow-2xl">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2.5">
                <div className="p-2 bg-rose-500/20 text-rose-300 border border-rose-500/40 rounded-xl">
                  <RotateCcw className="w-4 h-4" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="text-xs font-black uppercase text-rose-300 tracking-wide">
                      Bulk Re-Trigger Failed Verification Emails
                    </h4>
                    <span className="px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 text-[9px] font-black uppercase font-mono">
                      BATCH AUTOMATION
                    </span>
                  </div>
                  <p className="text-[11px] text-indigo-300">
                    Automatically re-dispatches fresh OTP codes and verification links to all bounced or failed athlete accounts across a selected date window.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsBulkRetryOpen(false)}
                className="text-indigo-400 hover:text-white text-xs font-bold"
              >
                ✕ Close
              </button>
            </div>

            {/* DATE RANGE PRESETS & CUSTOM INPUTS */}
            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1.5 flex items-center space-x-1">
                  <Calendar className="w-3.5 h-3.5 text-indigo-400" />
                  <span>Select Date Range Window:</span>
                </label>

                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                  {[
                    { id: '24h', label: 'Past 24 Hours' },
                    { id: '3d', label: 'Past 3 Days' },
                    { id: '7d', label: 'Past 7 Days' },
                    { id: '30d', label: 'Past 30 Days' },
                    { id: 'all', label: 'All History' },
                    { id: 'custom', label: 'Custom Range ⚙' },
                  ].map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => setBulkRetryPreset(preset.id as any)}
                      className={`py-2 px-2.5 rounded-xl font-black uppercase text-[10px] transition border text-center ${
                        bulkRetryPreset === preset.id
                          ? 'bg-rose-500 text-white border-rose-400 shadow-md ring-1 ring-rose-300'
                          : 'bg-indigo-900/60 hover:bg-indigo-900 text-indigo-200 border-white/10'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* CUSTOM DATE RANGE PICKERS */}
              {bulkRetryPreset === 'custom' && (
                <div className="p-3 bg-indigo-900/40 rounded-xl border border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fadeIn">
                  <div>
                    <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                      Start Date (From 00:00)
                    </label>
                    <input
                      type="date"
                      value={bulkRetryStartDate}
                      onChange={(e) => setBulkRetryStartDate(e.target.value)}
                      className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-rose-400 font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase text-indigo-300 mb-1">
                      End Date (To 23:59)
                    </label>
                    <input
                      type="date"
                      value={bulkRetryEndDate}
                      onChange={(e) => setBulkRetryEndDate(e.target.value)}
                      className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-rose-400 font-mono"
                    />
                  </div>
                </div>
              )}

              {/* CONTROLS & EXECUTION ROW */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end pt-1">
                <div className="sm:col-span-4">
                  <label className="block text-[10px] font-black uppercase text-indigo-300 mb-1">
                    Outgoing Delivery Provider
                  </label>
                  <select
                    value={bulkRetryProvider}
                    onChange={(e) => setBulkRetryProvider(e.target.value as any)}
                    className="w-full bg-indigo-900/80 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none font-bold"
                  >
                    <option value="fallback">Auto Provider (Resend → SMTP Fallback)</option>
                    <option value="resend">Force Resend API</option>
                    <option value="smtp">Force SMTP Mailer</option>
                  </select>
                </div>

                <div className="sm:col-span-4">
                  <div className="p-2.5 bg-indigo-900/80 rounded-xl border border-white/10 text-xs flex items-center justify-between">
                    <span className="text-[11px] text-indigo-200">Matching Failed Logs:</span>
                    <span className={`font-mono font-black text-sm px-2 py-0.5 rounded ${
                      matchingFailedLogs.length > 0 ? 'bg-rose-500/30 text-rose-300 border border-rose-500/40' : 'bg-emerald-500/20 text-emerald-300'
                    }`}>
                      {matchingFailedLogs.length} Failed
                    </span>
                  </div>
                </div>

                <div className="sm:col-span-4">
                  <button
                    type="button"
                    onClick={handleExecuteBulkRetry}
                    disabled={isExecutingBulkRetry}
                    className="w-full py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-black uppercase text-xs rounded-xl shadow-lg shadow-rose-600/30 transition flex items-center justify-center space-x-1.5 active:scale-95"
                  >
                    {isExecutingBulkRetry ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-white" />
                        <span>Processing Bulk Dispatch...</span>
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4" />
                        <span>Re-Trigger Failed Emails ({matchingFailedLogs.length || 'All'})</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* BULK RETRY EXECUTION RESULTS */}
            {bulkRetryResult && (
              <div className={`p-4 rounded-xl border space-y-3 text-xs animate-fadeIn ${
                bulkRetryResult.success
                  ? 'bg-emerald-950/40 border-emerald-400/30 text-emerald-200'
                  : 'bg-amber-950/40 border-amber-400/30 text-amber-200'
              }`}>
                <div className="flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="font-black uppercase flex items-center space-x-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>{bulkRetryResult.message || 'Bulk Re-Trigger Batch Summary'}</span>
                  </span>
                  <span className="font-mono text-[10px] text-indigo-300">
                    {bulkRetryResult.dateRange || 'Target Window'}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-mono">
                  <div className="bg-indigo-950/60 p-2 rounded-lg border border-white/10">
                    <span className="text-[9px] uppercase text-indigo-400 block font-sans">Processed</span>
                    <strong className="text-white text-base">{bulkRetryResult.processedCount ?? 0}</strong>
                  </div>
                  <div className="bg-emerald-950/60 p-2 rounded-lg border border-emerald-400/30">
                    <span className="text-[9px] uppercase text-emerald-400 block font-sans">Delivered</span>
                    <strong className="text-emerald-300 text-base">{bulkRetryResult.successCount ?? 0}</strong>
                  </div>
                  <div className="bg-rose-950/60 p-2 rounded-lg border border-rose-400/30">
                    <span className="text-[9px] uppercase text-rose-400 block font-sans">Failed</span>
                    <strong className="text-rose-300 text-base">{bulkRetryResult.failureCount ?? 0}</strong>
                  </div>
                </div>

                {bulkRetryResult.retryResults && bulkRetryResult.retryResults.length > 0 && (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    <span className="text-[10px] font-black uppercase text-indigo-300 block">
                      Individual Dispatch Results:
                    </span>
                    {bulkRetryResult.retryResults.map((r: any, idx: number) => (
                      <div
                        key={idx}
                        className="p-2 bg-indigo-950/80 rounded-lg border border-white/5 flex items-center justify-between text-[11px]"
                      >
                        <div className="flex items-center space-x-2">
                          {r.success ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          ) : (
                            <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                          )}
                          <span className="font-bold text-white">{r.email}</span>
                          {r.name && <span className="text-indigo-300">({r.name})</span>}
                        </div>
                        <div className="flex items-center space-x-2 font-mono text-[10px]">
                          {r.verificationCode && (
                            <span className="text-lime-300 font-bold bg-indigo-900 px-1.5 py-0.5 rounded">
                              OTP: {r.verificationCode}
                            </span>
                          )}
                          <span className="text-indigo-300 uppercase">{r.provider}</span>
                          <span className="text-amber-300">{r.latencyMs}ms</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* SEARCH & FILTERS BAR */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          <div className="sm:col-span-6 relative">
            <Search className="w-4 h-4 text-indigo-400 absolute left-3.5 top-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search email, athlete name, user ID, error code, OTP, or subject..."
              className="w-full bg-indigo-950 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-white text-xs outline-none focus:border-lime-400 placeholder:text-indigo-400"
            />
          </div>

          <div className="sm:col-span-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-lime-400 font-bold"
            >
              <option value="all">All Delivery Statuses ({logs.length})</option>
              <option value="delivered">Delivered Only ({deliveredCount})</option>
              <option value="failed">Failed Only ({failedCount})</option>
            </select>
          </div>

          <div className="sm:col-span-3">
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value as any)}
              className="w-full bg-indigo-950 border border-white/10 rounded-xl px-3 py-2 text-white text-xs outline-none focus:border-lime-400 font-bold"
            >
              <option value="all">All Providers</option>
              <option value="resend">Resend API</option>
              <option value="smtp">SMTP / Stream</option>
            </select>
          </div>
        </div>

        {/* LOGS TABLE LIST */}
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-indigo-950/40">
          <table className="w-full text-left text-xs text-indigo-100">
            <thead className="bg-indigo-950 text-[10px] uppercase font-black tracking-wider text-indigo-300 border-b border-white/10">
              <tr>
                <th className="py-3 px-4">Status & Provider</th>
                <th className="py-3 px-4">Recipient & User ID</th>
                <th className="py-3 px-4">Subject / Template</th>
                <th className="py-3 px-4">6-Digit Code</th>
                <th className="py-3 px-4">Timestamp & Latency</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 font-medium">
              {isLoadingLogs ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-indigo-300">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-lime-400 mb-2" />
                    <span>Loading real-time email logs from Firestore...</span>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-indigo-300">
                    <Mail className="w-8 h-8 mx-auto text-indigo-500 mb-2 opacity-50" />
                    <p className="font-bold text-white">No email delivery logs found matching filter.</p>
                    <p className="text-[11px] text-indigo-400 mt-1">
                      Trigger a test verification email or manual sync above to populate live records.
                    </p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const isExpanded = expandedLogId === log.id;
                  const isSyncingThis = syncingRowId === log.id;
                  const isSyncedThis = syncedRowSuccessId === log.id;
                  const formattedTime = new Date(log.timestamp || log.createdAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  });

                  return (
                    <React.Fragment key={log.id}>
                      <tr className="hover:bg-white/5 transition">
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {log.status === 'delivered' ? (
                              <span className="p-1 bg-lime-400/20 text-lime-400 rounded-lg">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              </span>
                            ) : (
                              <span className="p-1 bg-rose-500/20 text-rose-400 rounded-lg">
                                <AlertTriangle className="w-3.5 h-3.5" />
                              </span>
                            )}
                            <div>
                              <div className="flex items-center space-x-1.5">
                                <span className={`text-[10px] font-black uppercase tracking-wide ${
                                  log.status === 'delivered' ? 'text-lime-300' : 'text-rose-300'
                                }`}>
                                  {log.status}
                                </span>
                                {log.errorCode && (
                                  <span className="px-1.5 py-0.2 bg-rose-500/30 text-rose-300 border border-rose-500/40 rounded text-[8px] font-mono font-black uppercase">
                                    {log.errorCode}
                                  </span>
                                )}
                              </div>
                              <span className="text-[9px] font-mono text-indigo-400 uppercase">
                                {log.provider}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="font-bold text-white flex items-center space-x-1.5">
                            <span>{log.recipientEmail}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-[10px] text-indigo-300">
                            {log.recipientName && <span>{log.recipientName}</span>}
                            {log.userId && (
                              <span className="font-mono text-[9px] bg-indigo-900/60 px-1.5 py-0.5 rounded border border-white/10 text-indigo-200">
                                UID: {log.userId}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-4 max-w-[240px] truncate">
                          <span className="font-semibold text-indigo-100">{log.subject}</span>
                          <span className="text-[10px] text-indigo-400 block uppercase font-bold">
                            {log.type.replace('_', ' ')}
                            {log.triggeredBy && ` • ${log.triggeredBy.replace('_', ' ')}`}
                          </span>
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap">
                          {log.verificationCode ? (
                            <div className="flex items-center space-x-1.5">
                              <span className="font-mono font-black text-lime-300 bg-indigo-950 px-2 py-0.5 rounded border border-lime-400/30 text-xs">
                                {log.verificationCode}
                              </span>
                              <button
                                onClick={() => handleCopy(log.verificationCode!, `code_${log.id}`)}
                                className="p-1 text-indigo-400 hover:text-lime-300 transition"
                                title="Copy 6-digit OTP code"
                              >
                                {copiedId === `code_${log.id}` ? <Check className="w-3 h-3 text-lime-400" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                          ) : (
                            <span className="text-indigo-500 text-[10px]">—</span>
                          )}
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="text-white text-[11px]">{formattedTime}</div>
                          <div className="text-[10px] text-amber-300/80 font-mono">
                            {log.latencyMs ? `${log.latencyMs}ms` : '—'}
                          </div>
                        </td>

                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end space-x-1.5">
                            {/* SYNC NOW BUTTON PER ROW */}
                            <button
                              type="button"
                              onClick={() => handleRowSyncNow(log)}
                              disabled={isSyncingThis}
                              className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition flex items-center space-x-1 border ${
                                isSyncedThis
                                  ? 'bg-lime-400 text-black border-lime-300'
                                  : 'bg-amber-400/20 hover:bg-amber-400 hover:text-black text-amber-300 border-amber-400/40'
                              }`}
                              title="Sync Now: Re-trigger fresh verification email"
                            >
                              {isSyncingThis ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span>Syncing</span>
                                </>
                              ) : isSyncedThis ? (
                                <>
                                  <Check className="w-3 h-3 text-black" />
                                  <span>Synced!</span>
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="w-3 h-3" />
                                  <span>Sync Now</span>
                                </>
                              )}
                            </button>

                            <button
                              onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                              className="p-1.5 text-indigo-300 hover:text-white bg-indigo-900/50 hover:bg-indigo-800 rounded-lg transition"
                              title="View full payload details"
                            >
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => handleDeleteLog(log.id)}
                              className="p-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-500/20 rounded-lg transition"
                              title="Delete log item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* EXPANDED ROW METADATA */}
                      {isExpanded && (
                        <tr className="bg-indigo-950/80">
                          <td colSpan={6} className="p-4 border-t border-white/5 space-y-2">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                              <div>
                                <span className="text-[10px] uppercase font-bold text-indigo-400 block">Log ID:</span>
                                <span className="font-mono text-indigo-200 text-[11px]">{log.id}</span>
                              </div>
                              <div>
                                <span className="text-[10px] uppercase font-bold text-indigo-400 block">User ID:</span>
                                <span className="font-mono text-lime-300 text-[11px]">{log.userId || 'Anonymous / Unlinked'}</span>
                              </div>
                              <div>
                                <span className="text-[10px] uppercase font-bold text-indigo-400 block">Provider Message ID:</span>
                                <span className="font-mono text-indigo-200 text-[11px]">{log.providerMessageId || 'N/A'}</span>
                              </div>
                              {log.metadata?.directVerifyUrl && (
                                <div>
                                  <span className="text-[10px] uppercase font-bold text-indigo-400 block">Direct Verification URL:</span>
                                  <a
                                    href={log.metadata.directVerifyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-lime-300 hover:underline flex items-center space-x-1 text-[11px] truncate"
                                  >
                                    <span>Open Verify Link</span>
                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                  </a>
                                </div>
                              )}
                            </div>

                            {log.errorCode && (
                              <div className="p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-300 text-xs flex items-center space-x-2">
                                <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
                                <div>
                                  <span className="font-black uppercase text-[10px] block">Error Code Identifier:</span>
                                  <span className="font-mono font-bold">{log.errorCode}</span>
                                </div>
                              </div>
                            )}

                            {log.errorMessage && (
                              <div className="p-3 bg-rose-950/50 border border-rose-500/40 rounded-xl text-rose-300 text-xs">
                                <span className="font-bold uppercase text-[10px] block mb-1">Diagnostic Details:</span>
                                <code>{log.errorMessage}</code>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODAL: BRANDED EMAIL TEMPLATE PREVIEW ================= */}
      <EmailTemplatePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        onTriggerToast={onTriggerToast}
      />
    </div>
  );
};
